import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "../../services/authService";
import Toast from "./Toast";
import LoginInput from "./LoginInput";
import RememberForgot from "./RememberForgot";
import { jwtDecode } from "jwt-decode";
import "../../styles/log.css";
import { requestNotificationPermission } from "../../services/notificationService";
import { getStudentNotifications } from "../../services/notificationServices";
import { getOldOpenRequirements, sendEscalationMail } from "../../services/placementService";
import showOldRequirementPopup from "../placement/OldRequirementPopup";

const LoginForm = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const updateForm = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const [deactivatedPopup, setDeactivatedPopup] = useState(false);
  const showDeactivatedPopup = () => setDeactivatedPopup(true);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateInput = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_])[A-Za-z\d@$!%*?&_]{8,16}$/;
    const isStudent = form.email.toLowerCase().includes("@gmail"); // adjust this check based on how you identify students

    if (!emailRegex.test(form.email)) {
      return "Enter a valid email ID";
    }

    if (!isStudent && !passwordRegex.test(form.password)) {
      return "Password must contain 1 uppercase, 1 lowercase, 1 special character, and be 8-16 characters long";
    }

    return null;
  };


  const handleLogin = async (e) => {
    e.preventDefault();

    const errorMessage = validateInput();
    if (errorMessage) return showToast(errorMessage, "error");

    // ── STAFF LOGIN ──────────────────────────────────────────────
    try {
      const token = await login(form.email, form.password);
      const decoded = jwtDecode(token);
      const role = decoded.role?.toLowerCase();

      showToast("Login successful!", "success");

      try {
        const oldReqData = await getOldOpenRequirements();
        if (oldReqData?.success && oldReqData?.data?.length > 0) {
          const popupResult = await showOldRequirementPopup(oldReqData.data);
          if (popupResult.isConfirmed) {
            try {
              await sendEscalationMail(oldReqData.data);
            } catch (mailError) {
              console.error("Mail sending failed:", mailError);
            }
          }
        }
      } catch (popupError) {
        console.error("Popup fetch error:", popupError);
      }

      if (role === "trainer") {
        navigate("/staff-dashboard", { replace: true });
      } else if (role === "branch-head" || role === "super-admin") {
        navigate("/landingpage", { replace: true });
      } else if (role === "bde") {
        navigate("/dashboard", { replace: true });
      } else if (role === "placement officer") {
        navigate("/placementdb", { replace: true });
      } else {
        showToast("Access denied: unauthorized role", "error");
      }
      return; // ← stop here, don't fall into student login

    } catch (staffError) {
      const status = staffError?.response?.status;
      const serverMsg = staffError?.response?.data?.message || "";

      // ── DEACTIVATED ACCOUNT: show popup, do NOT try student login ──
      if (status === 403 || serverMsg.toLowerCase().includes("deactivated")) {
        showDeactivatedPopup();
        return;
      }

      // ── WRONG CREDENTIALS / NOT FOUND: fall through to student login ──
      // (404 = user not found in staff table → try student)
      // (401 = wrong password for a staff account → also try student,
      //  because the email may belong to a student, not staff)
    }

    // ── STUDENT LOGIN ────────────────────────────────────────────
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/logined`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_Id: form.email, password: form.password }),
      });

      const data = await res.json();

      if (!data.success) {
        return showToast(data.message || "Login failed", "error");
      }

      // First-time login → setup password
      if (data.requirePasswordSetup) {
        showToast("First time login – please set up your password", "info");
        localStorage.setItem("studentData", JSON.stringify(data.student));
        setTimeout(() => navigate("/setup-password", { state: { student: data.student } }), 1000);
        return;
      }

      // Normal student login
      showToast("Student login successful!", "success");
      localStorage.setItem("studentData", JSON.stringify(data.student));
      localStorage.setItem("studentToken", data.token);
      requestNotificationPermission();

      try {
        const notifData = await getStudentNotifications();
        const list = Array.isArray(notifData) ? notifData : notifData?.data ?? [];
        const pending = list.filter(
          (n) => n.acknowledgementStatus?.toLowerCase().trim() === "pending"
        ).length;

        setTimeout(() => {
          navigate("/stu-dashboard", {
            state: { student: data.student },
          });
        }, 1000);

      } catch (notifErr) {
        console.error("Notification fetch failed:", notifErr);
        navigate("/stu-dashboard", { state: { student: data.student } });
      }

    } catch (studentErr) {
      console.error("Student login error:", studentErr);
      showToast("Login failed. Please try again.", "error");
    }
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false }), 3000);
  };

  return (
    <div className="body">
      {toast.show && <Toast message={toast.message} type={toast.type} />}
      <div className="login-container">
        <form onSubmit={handleLogin}>
          <div className="logo">
            <img src="/images/logo.png" alt="PUMO TECHNOVATION" />
          </div>
          <h2>PUMO <span>TECHNOVATION</span></h2>

          <LoginInput
            name="email"
            type="text"
            placeholder=" Email ID"
            value={form.email}
            onChange={updateForm}
          />

          <div className="password-input-container">
            <LoginInput
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={form.password}
              onChange={updateForm}
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>

          <RememberForgot />

          <button type="submit" className="btn1">LOGIN</button>
        </form>
      </div>
      {deactivatedPopup && (
        <div className="deactivated-overlay">
          <div className="deactivated-popup">
            <div className="popup-icon">🔒</div>
            <h3>Account Deactivated</h3>
            <p className="popup-message">Your account has been deactivated.</p>
            <p className="popup-contact">
              Please contact: <strong>Hari</strong><br />
              📞 9025680801
            </p>
            <button
              className="popup-ok-btn"
              onClick={() => setDeactivatedPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;